import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SearchInput from '../components/common/SearchInput';
import Pagination from '../components/common/Pagination';
import ByAlbumsTable from '../components/ByAlbumsTable';
import { useSelector } from 'react-redux';
import { authSelector } from '../features/auth/slice';
import SectionTabs from '../components/SectionTabs';
import { Users, Disc, Music, Globe, User, Library, ListMusic, Package, Boxes } from 'lucide-react';
import usePhysicalExists from '../hooks/usePhysicalExists';

function ByAlbumsPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const isAdmin = me?.adminYn === 'Y';
  const [loading, setLoading] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  const fetchAlbums = useCallback(
    async (page, search) => {
      const clientId = me.id;
      const adminYn = me.adminYn === 'Y' ? 'Y' : 'N';

      setLoading(true);
      try {
        const res = await axios.get(`/ByAlbum/byAlbums`, {
          params: { page, limit: pageSize, search, clientId, adminYn },
        });
        setAlbums(res.data.items);
        setTotalPages(res.data.totalPages);
        setCurrentPage(res.data.currentPage);
        setTotalCount(res.data.totalCount);
      } catch (error) {
        console.error('서버 접속 에러:', error);
      } finally {
        setLoading(false);
      }
    },
    [me]
  );

  useEffect(() => {
    if (!me) return;
    fetchAlbums(1, '');
  }, [me, fetchAlbums]);

  useEffect(() => {
    if (!me) return;
    fetchAlbums(currentPage, searchKeyword);
  }, [me, currentPage, searchKeyword, fetchAlbums]);

  const handlePageChange = (p) => {
    setCurrentPage(p);
  };

  const hasPhysical = usePhysicalExists();
  const reportTabs = [
    { label: isAdmin ? '당월권리사별' : '월별리포트', to: '/ByClientLatestMonth', icon: Users },
    { label: '당월앨범별', to: '/ByMonthAlbums', icon: Disc },
    { label: '당월곡별', to: '/ByMonthSongs', icon: Music },
    { label: '당월플랫폼별', to: '/ByMonthPlatforms', icon: Globe },
    { label: '당월아티스트별', to: '/ByMonthArtists', icon: User },
    ...(hasPhysical ? [{ label: '당월음반별', to: '/ByPhysicalMonth', icon: Package }] : []),
    { label: '앨범별누적', to: '/ByAlbums', icon: Library },
    { label: '곡별누적', to: '/BySongs', icon: ListMusic },
    ...(hasPhysical ? [{ label: '음반누적', to: '/ByPhysicalTotal', icon: Boxes }] : []),
  ];

  return (
    <>
        <div className="flex mb-8 justify-center items-center">
          <h2 className="text-xl sm:text-2xl font-bold">앨범별 누적 정산</h2>
        </div>
        <SectionTabs items={reportTabs} />
        <SearchInput
          placeholder="앨범명 또는 아티스트명 검색"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onSearch={() => {
            setCurrentPage(1); // 검색할 땐 페이지도 초기화
            setSearchKeyword(searchTerm);
          }}
        />

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3"></div>
            <div className="text-sm text-slate-500">데이터 불러오는 중...</div>
          </div>
        ) : (
          <>
            <ByAlbumsTable
              data={albums}
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              me={me}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
    </>
  );
}

export default ByAlbumsPage;
